(() => {
  if (window.Grada90ReferralCapture) {
    window.Grada90ReferralCapture.refresh();
    return;
  }

  const STORAGE_KEY = 'grada90_referral';
  const COOKIE_NAME = 'grada90_referral';
  const CART_ATTRIBUTE = '_referral_code';
  const LEGACY_ATTRIBUTE = 'Código de referido';
  const VALIDATION_PATH = '/apps/grada90-referidos/validate';
  const MAX_AGE_SECONDS = 7 * 24 * 60 * 60;
  const CODE_PATTERN = /^G90-[A-Z0-9]{6}$/;

  let activeCode = '';
  let draftCode = '';
  let message = '';
  let messageState = '';
  let applying = false;
  let initialized = false;

  const normalizeCode = (value) => String(value || '').trim().toUpperCase();
  const hasValidFormat = (code) => CODE_PATTERN.test(code);

  function parseStoredValue(rawValue) {
    if (!rawValue) return null;
    try {
      const stored = JSON.parse(rawValue);
      const code = normalizeCode(stored.code);
      const expiresAt = Number(stored.expiresAt);
      return hasValidFormat(code) && expiresAt > Date.now() ? { code, expiresAt } : null;
    } catch (_error) {
      return null;
    }
  }

  function readCookie() {
    const prefix = `${COOKIE_NAME}=`;
    const part = document.cookie
      .split(';')
      .map((item) => item.trim())
      .find((item) => item.startsWith(prefix));
    return part ? decodeURIComponent(part.slice(prefix.length)) : '';
  }

  function readStoredReferral() {
    let localValue = '';
    try {
      localValue = localStorage.getItem(STORAGE_KEY) || '';
    } catch (_error) {
      // La cookie actúa como respaldo si localStorage no está disponible.
    }
    return parseStoredValue(localValue) || parseStoredValue(readCookie());
  }

  function persistReferral(code) {
    const storedValue = JSON.stringify({
      code,
      expiresAt: Date.now() + MAX_AGE_SECONDS * 1000,
    });
    try {
      localStorage.setItem(STORAGE_KEY, storedValue);
    } catch (_error) {
      // La cookie mantiene la persistencia.
    }
    const secure = location.protocol === 'https:' ? '; Secure' : '';
    document.cookie = `${COOKIE_NAME}=${encodeURIComponent(storedValue)}; Max-Age=${MAX_AGE_SECONDS}; Path=/; SameSite=Lax${secure}`;
    activeCode = code;
    draftCode = '';
  }

  function clearStoredReferral() {
    try {
      localStorage.removeItem(STORAGE_KEY);
    } catch (_error) {
      // La cookie también se elimina a continuación.
    }
    document.cookie = `${COOKIE_NAME}=; Max-Age=0; Path=/; SameSite=Lax`;
    activeCode = '';
  }

  async function validateCode(code) {
    const response = await fetch(`${VALIDATION_PATH}?referralCode=${encodeURIComponent(code)}`, {
      headers: { Accept: 'application/json' },
    });
    if (!response.ok) throw new Error('No se pudo comprobar el código. Inténtalo de nuevo.');
    const result = await response.json();
    return result.valid === true;
  }

  async function syncCart(code) {
    const root = window.Shopify?.routes?.root || '/';
    const response = await fetch(`${root}cart/update.js`, {
      method: 'POST',
      headers: { Accept: 'application/json', 'Content-Type': 'application/json' },
      body: JSON.stringify({
        attributes: {
          [CART_ATTRIBUTE]: code || '',
          [LEGACY_ATTRIBUTE]: '',
        },
      }),
    });
    if (!response.ok) throw new Error('No se pudo asociar el código al carrito.');
  }

  function render(nextMessage, nextState) {
    if (nextMessage !== undefined) {
      message = nextMessage;
      messageState = nextState || '';
    }
    document.querySelectorAll('[data-grada90-referral-block]').forEach((block) => {
      const input = block.querySelector('[data-grada90-referral-input]');
      const button = block.querySelector('[data-grada90-referral-apply]');
      const status = block.querySelector('[data-grada90-referral-status]');
      if (input && document.activeElement !== input) input.value = draftCode || activeCode;
      if (button) {
        button.disabled = applying;
        button.textContent = applying ? 'Validando…' : 'Aplicar';
      }
      if (status) {
        status.textContent = message || (activeCode ? 'Código aplicado y asociado al pedido.' : '');
        status.dataset.state = messageState || (activeCode ? 'success' : '');
      }
    });
  }

  async function applyCode(rawCode, successMessage = 'Código aplicado y asociado al pedido.') {
    const code = normalizeCode(rawCode);
    if (!hasValidFormat(code)) {
      render('Formato incorrecto. Debe ser G90- seguido de 6 letras o números.', 'error');
      return false;
    }
    if (!(await validateCode(code))) {
      render('El código no existe o ya no está activo.', 'error');
      return false;
    }
    await syncCart(code);
    persistReferral(code);
    render(successMessage, 'success');
    return true;
  }

  async function initialize() {
    if (initialized) return;
    initialized = true;

    const stored = readStoredReferral();
    if (stored) {
      if (await validateCode(stored.code)) {
        activeCode = stored.code;
        await syncCart(activeCode);
      } else {
        clearStoredReferral();
        await syncCart('');
      }
    }

    const urlValue = new URLSearchParams(location.search).get('ref');
    if (urlValue !== null) {
      const applied = await applyCode(urlValue, 'Código del enlace aplicado y asociado al pedido.');
      if (!applied && activeCode) await syncCart(activeCode);
    } else if (!stored && document.querySelector('[data-grada90-referral-block]')) {
      // Limpia el atributo anterior, que no estaba conectado con la app.
      await syncCart('');
    }
    render();
  }

  document.addEventListener('input', (event) => {
    const input = event.target.closest?.('[data-grada90-referral-input]');
    if (!input) return;
    draftCode = input.value;
    render('', '');
  });

  document.addEventListener('keydown', (event) => {
    if (event.key !== 'Enter' || !event.target.matches?.('[data-grada90-referral-input]')) return;
    event.preventDefault();
    event.target.closest('[data-grada90-referral-block]')?.querySelector('[data-grada90-referral-apply]')?.click();
  });

  document.addEventListener('click', async (event) => {
    const button = event.target.closest?.('[data-grada90-referral-apply]');
    if (!button || applying) return;
    const input = button.closest('[data-grada90-referral-block]')?.querySelector('[data-grada90-referral-input]');
    applying = true;
    render('Comprobando el código con GRADA90 Referidos…', '');
    try {
      await applyCode(input?.value || '');
    } catch (error) {
      render(error instanceof Error ? error.message : 'No se pudo aplicar el código.', 'error');
    } finally {
      applying = false;
      render();
    }
  });

  new MutationObserver((records) => {
    const blockAdded = records.some((record) =>
      Array.from(record.addedNodes).some(
        (node) =>
          node instanceof Element &&
          (node.matches('[data-grada90-referral-block]') || node.querySelector('[data-grada90-referral-block]'))
      )
    );
    if (blockAdded) render();
  }).observe(document.documentElement, { childList: true, subtree: true });

  window.Grada90ReferralCapture = { refresh: render };
  if (document.readyState === 'loading') {
    document.addEventListener(
      'DOMContentLoaded',
      () => initialize().catch((error) => render(error.message, 'error')),
      { once: true }
    );
  } else {
    initialize().catch((error) => render(error.message, 'error'));
  }
})();
